import { Markierung } from '@/domain/termine/components';
import {
  markierungMapper,
  MarkierungType,
  Termin,
} from '@/domain/termine/shared/types';
import { formatTerminDateTime } from '@/domain/termine/shared/utils/formatDateTime';
import { ReactNode } from 'react';

type VerknuepfungsDetailItem = { label: string; color: string };

type VerknuepfungsDetails = {
  schichten: VerknuepfungsDetailItem[];
  arbeitszeiten: VerknuepfungsDetailItem[];
};

type BuildDeleteErrorOutputProps = {
  message: string;
  termin?: Termin;
  isVerknuepfungsError: boolean;
  labels: {
    schichtOne: string;
    schichtOther: string;
    azOne: string;
    azOther: string;
  };
};

function toDetailItem(
  name: string,
  color: string,
  datum: string,
  beginn?: string | null,
  ende?: string | null,
): VerknuepfungsDetailItem {
  return {
    label: `${name} · ${formatTerminDateTime(datum, beginn, ende)}`,
    color,
  };
}

function getMarkierungColor(markierung?: MarkierungType | null): string {
  const color = markierung ? markierungMapper[markierung]?.base : '#ccc';
  return markierung ? color : markierungMapper.GRAY.base;
}

function buildVerknuepfungsSummaryMessage(
  details: VerknuepfungsDetails,
  labels: BuildDeleteErrorOutputProps['labels'],
): string {
  const createPart = (count: number, one: string, other: string) =>
    `${count} ${count === 1 ? one : other}verknüpfung${count === 1 ? '' : 'en'}`;
  const parts: string[] = [];

  if (details.schichten.length > 0) {
    parts.push(
      createPart(
        details.schichten.length,
        labels.schichtOne,
        labels.schichtOther,
      ),
    );
  }
  if (details.arbeitszeiten.length > 0) {
    parts.push(
      createPart(details.arbeitszeiten.length, labels.azOne, labels.azOther),
    );
  }
  return `${parts.join(' und ')} vorhanden`;
}

function getVerknuepfungsDetails(
  termin: Termin | undefined,
  isVerknuepfungsError: boolean,
): VerknuepfungsDetails {
  if (!termin || !isVerknuepfungsError) {
    return { schichten: [], arbeitszeiten: [] };
  }
  const schichten = (termin.schichten ?? []).map((schicht) =>
    toDetailItem(
      schicht.name,
      getMarkierungColor(schicht.markierung),
      schicht.datum,
      schicht.beginn,
      schicht.ende,
    ),
  );
  const arbeitszeiten = (termin.arbeitszeiten ?? []).map((arbeitszeit) =>
    toDetailItem(
      arbeitszeit.arbeitsbereich.name,
      getMarkierungColor(arbeitszeit.arbeitsbereich.markierung),
      arbeitszeit.datum,
      arbeitszeit.beginn,
      arbeitszeit.ende,
    ),
  );
  return { schichten, arbeitszeiten };
}

function renderDetailRow(item: VerknuepfungsDetailItem, key: string) {
  return (
    <div key={key} className="py-2 flex items-center gap-1">
      <span className="inline-flex scale-75">
        <Markierung color={item.color} />
      </span>
      <span>{item.label}</span>
    </div>
  );
}

function renderVerknuepfungsDetailList(
  list: VerknuepfungsDetailItem[],
): ReactNode[] {
  return list.flatMap((item, index) =>
    index < list.length - 1
      ? [
          renderDetailRow(item, `${item.label}-${index}`),
          <div
            key={`separator-${item.label}-${index}`}
            className="h-px bg-neutrals-300"
          />,
        ]
      : [renderDetailRow(item, `${item.label}-${index}`)],
  );
}

function buildOneVerknuepfung(
  label: string,
  details: VerknuepfungsDetailItem[],
): ReactNode | null {
  if (details.length === 0) return null;
  return (
    <div className="space-y-1">
      <div className="text-xs font-medium mt-1">{label}</div>
      <div className="text-xs">{renderVerknuepfungsDetailList(details)}</div>
    </div>
  );
}

function buildMultipleVerknuepfungen(
  details: VerknuepfungsDetails,
  labels: BuildDeleteErrorOutputProps['labels'],
): ReactNode[] {
  const parts: ReactNode[] = [];
  if (details.schichten.length > 0) {
    const schichtenLabel =
      details.schichten.length === 1 ? labels.schichtOne : labels.schichtOther;
    parts.push(buildOneVerknuepfung(schichtenLabel, details.schichten));
  }
  if (details.arbeitszeiten.length > 0) {
    const arbeitszeitenLabel =
      details.arbeitszeiten.length === 1 ? labels.azOne : labels.azOther;
    parts.push(buildOneVerknuepfung(arbeitszeitenLabel, details.arbeitszeiten));
  }
  return parts;
}

function buildVerknuepfungsToastDescription(
  baseDescription: string,
  details: VerknuepfungsDetails,
  labels: BuildDeleteErrorOutputProps['labels'],
): ReactNode {
  const allDetails = [...details.schichten, ...details.arbeitszeiten];
  const hasBothTypes =
    details.schichten.length > 0 && details.arbeitszeiten.length > 0;

  if (allDetails.length === 0) return baseDescription;
  if (allDetails.length <= 2 && !hasBothTypes) {
    return (
      <div className="space-y-1">
        <div>{baseDescription}</div>
        <div className="text-xs">
          {renderVerknuepfungsDetailList(allDetails)}
        </div>
      </div>
    );
  }
  const parts = buildMultipleVerknuepfungen(details, labels);

  return (
    <div className="space-y-2">
      <div>{baseDescription}</div>
      {parts}
    </div>
  );
}

export function buildDeleteErrorOutput({
  message,
  termin,
  isVerknuepfungsError,
  labels,
}: BuildDeleteErrorOutputProps): {
  errorMessage: string;
  toastDescription: ReactNode;
} {
  const verknuepfungsDetails = getVerknuepfungsDetails(
    termin,
    isVerknuepfungsError,
  );
  const allVerknuepfungen = [
    ...verknuepfungsDetails.schichten,
    ...verknuepfungsDetails.arbeitszeiten,
  ];

  if (allVerknuepfungen.length === 0) {
    return { errorMessage: message, toastDescription: message };
  }

  const summaryMessage = buildVerknuepfungsSummaryMessage(
    verknuepfungsDetails,
    labels,
  );

  return {
    errorMessage: `${summaryMessage}: ${allVerknuepfungen.map((item) => item.label).join(', ')}`,
    toastDescription: buildVerknuepfungsToastDescription(
      summaryMessage,
      verknuepfungsDetails,
      labels,
    ),
  };
}
